import { between, integer } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import type { FlowOperatorMetadata, ValidationResult, Period } from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import { includesValuesValidator, requiredWithMessage } from '@/helpers/CustomValidator';
import Form from './Form.vue';

export interface DelaisMaxData {
  time_unit_select: Period;
}

export const createDefaultData = (overrides: Partial<DelaisMaxData> = {}): DelaisMaxData => ({
  time_unit_select: {
    value: 10,
    unit: 'MINUTE',
  },
  ...overrides,
});

export const DelaisMax: FlowOperatorMetadata<DelaisMaxData> = {
  id: 'boxdelaismax',
  kind: 'action',
  label: 'flows.operators.delaisMax.label',
  icon: 'fa-alarm-clock',
  hasConfiguration: true,

  availableIn: [TypeCampaignEnum.AUTOMATION],

  incompatibilities: {
    automation: {
      nextOperators: [
        'boxabandonpanier',
        'boxsitepasstime',
        'boxperiodiclaunching',
        'boxwait',
        'boxtag',
        'boxlist',
        'boxsendemail',
        'boxsendsms',
        'boxabtest',
        'boxdisplayfreepopup',
        'boxdisplayfreemodel',
        'boxsendfbmessenger',
        'boxsendpushnotification',
        'boxfiltre_1',
        'boxfiltre_2',
        'boxfiltre_3',
        'boxfiltre_4',
        'openemail',
        'clicklink',
        'boxnavigationtrigger',
        'boxdelaismax',
        'boxsendrcs',
        'boxsendwhatsapp',
        'boxcontactseg',
      ],
    },
    bulk: {
      nextOperators: [
        'boxabandonpanier',
        'boxsitepasstime',
        'boxperiodiclaunching',
        'boxwait',
        'boxtag',
        'boxlist',
        'boxsendemail',
        'boxsendsms',
        'boxabtest',
        'boxdisplayfreepopup',
        'boxdisplayfreemodel',
        'boxsendfbmessenger',
        'boxsendpushnotification',
        'boxfiltre_1',
        'boxfiltre_2',
        'boxfiltre_3',
        'boxfiltre_4',
        'openemail',
        'clicklink',
        'boxnavigationtrigger',
        'boxdelaismax',
        'boxsendrcs',
        'boxsendwhatsapp',
        'boxcontactseg',
      ],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs(data: DelaisMaxData): number {
    return 1;
  },

  getSubtitle: (data: DelaisMaxData, t) => {
      // Handle JSON string format
      let timeUnitSelect = data.time_unit_select as unknown;
      if (typeof timeUnitSelect === 'string') {
        try {
          timeUnitSelect = JSON.parse(timeUnitSelect);
        } catch {
          return null;
        }
      }
      
      const parsed = timeUnitSelect as { value?: number; unit?: string } | null;
      const { value, unit } = parsed || {};
      if (!value || !unit) return null;
      
      // Map unit to translation key (singular/plural)
      const unitKeyMap: Record<string, { singular: string; plural: string }> = {
        SECOND: { singular: 'second', plural: 'seconds' },
        MINUTE: { singular: 'minute', plural: 'minutes' },
        HOUR: { singular: 'hour', plural: 'hours' },
        DAY: { singular: 'day', plural: 'days' },
        MONTH: { singular: 'month', plural: 'month' },
      };
      const keys = unitKeyMap[unit] || { singular: unit.toLowerCase(), plural: unit.toLowerCase() };
      const unitKey = value === 1 ? keys.singular : keys.plural;
      const unitLabel = t(`automatedScenarios.actions.wait.units.${unitKey}`);
      
      return `${value} ${unitLabel}`;
    },

  async validate(data: DelaisMaxData): Promise<ValidationResult> {
    const rules = {
      time_unit_select: {
        value: {
          requiredWithMessage: requiredWithMessage(),
          integer,
          between: between(1, 1000),
        },
        unit: {
          requiredWithMessage: requiredWithMessage(),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
    };

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();

    return {
      success,
      validate: v$,
    };
  },
};

export default DelaisMax;
